import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Icon from '@cx-web/components/primitives/icon';
import Title, { Header } from '@cx-web/components/primitives/title';
import { type BetslipProviderData, type CallbackFn, useBetslipContext } from '@cx-web/feature-betslip/context';
import { type ILabelActions, useLabelActions } from '@cx-web/feature-i18n';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS, trackingActions } from '@cx-web/feature-tracking';
import { captureException } from '@sentry/core';

import { type Country, type Repub, REPUBS } from '@cx-modules/constants';

import ButtonSection from './components/button-section';
import ClickoutErrorModal from './components/clickout-error-modal';
import MultiBetslip from './components/multi-betslip';
import EmptyBetslip from './icons/empty-betslip';
import LoadingBetslip from './icons/loading-betslip';
import styles from './style/betslip.module.css';

interface WithContext {
  betslipContext: BetslipProviderData;
  labelActions: ILabelActions;
  repub: Repub;
}

interface IBetslipProps extends WithContext {
  className?: string;
  country?: Country;
}

/**
 * Betslip ARIA navigation contract — for screen readers and automated tests.
 *
 * Entry point: locate the betslip region by accessible name.
 *     region = page.getByRole('region', { name: 'Betslip' })
 *
 * The drawer is always mounted; closed/open is exposed via aria-hidden, with
 * data-betslip-open and data-betslip-count as backup selectors:
 *     await expect(region).toHaveAttribute('aria-hidden', 'false') // open
 *     await expect(region).toHaveAttribute('aria-hidden', 'true')  // closed
 *
 * The toggle that opens/closes the drawer is the mini-betslip in the header.
 * Its accessible name is the `betslipTitle` prop (a localized "Betslip" label),
 * with the current count appended once at least one bet is on the slip
 * (e.g. "Betslip, 3 bets"):
 *     toggle = page.getByRole('button', { name: <betslipTitle> })
 *     await toggle.click()
 *     await expect(toggle).toHaveAttribute('aria-expanded', 'true')
 *     await expect(toggle).toHaveAccessibleName(/3 bets/)
 *
 * The region exposes its fetch state via aria-busy. Wait on this before
 * asserting bet contents — the underlying /sports-catalogue/v1/betslip call is
 * async and there is no other reliable settle signal:
 *     await expect(region).toHaveAttribute('aria-busy', 'false')
 *
 * While loading, a status panel is announced ("Loading bets"):
 *     await expect(page.getByRole('status', { name: /loading bets/i })).toBeVisible()
 *
 * To read how many bets are currently on the slip, there are four redundant
 * paths — pick whichever fits the test framework best:
 *     // 1. labelled count badge (live region, announces on change)
 *     await expect(page.getByRole('status', { name: /bets selected/i }))
 *       .toHaveAccessibleName('3 bets selected')
 *     // 2. labelled list container — name encodes the count
 *     await expect(region.getByRole('list', { name: /Selected bets \(3\)/ }))
 *       .toBeVisible()
 *     // 3. count the listitems directly
 *     await expect(region.getByRole('listitem')).toHaveCount(3)
 *     // 4. data attribute fallback
 *     await expect(region).toHaveAttribute('data-betslip-count', '3')
 *
 * Each bet is a <li> whose accessible name is composed as
 * `<bet name> — <subevent> — <market>`. It also carries aria-posinset and
 * aria-setsize so screen readers announce "bet 2 of 3" and tests can locate
 * a row by ordinal position. Target a specific bet by accessible name, by
 * stable data-bet-id when the betId is known, or by position:
 *     bet = page.getByRole('listitem', { name: /Liverpool/i })
 *     bet = page.locator('[data-bet-id="26391777431"]')
 *     bet = page.locator('[aria-posinset="2"]')
 *
 * Each bet row contains a remove button keyed off the same accessible name:
 *     await page.getByRole('button', { name: /Remove Liverpool .* from betslip/i }).click()
 *
 * The close-betslip button lives inside the region header:
 *     await page.getByRole('button', { name: 'Close betslip' }).click()
 *
 * Diagnostic shim (non-OC repubs only; gated by ?cxDebug=1 query param or
 * cxDebug=1 cookie — see packages/features/betslip/src/context/betslip-context.tsx):
 *     window.__cxBetslip.clear()           — wipes cookies + localStorage + state
 *     window.__cxBetslip.lastFetchStatus() — { ok, reason, at } of last fetch,
 *                                            useful when aria-busy never flips
 */
class Betslip extends React.Component<IBetslipProps> {
  constructor(public props: IBetslipProps) {
    super(props);

    this.setupCallbacksListener();
  }

  private setupCallbacksListener = () => {
    if (typeof window === 'undefined') return;
    const { betslipContext, repub } = this.props;

    // OC owns window.ocBetslip (CDN / integrated widget); do not plant a partial stub here.
    if (repub === REPUBS.OC.name) return;

    const allCallbacks = betslipContext.callbacksRef.current;

    window.ocBetslip = window.ocBetslip || {};
    window.ocBetslip.setCallbacks = (callbacks: Record<string, CallbackFn>) => {
      Object.entries(callbacks).forEach(([key, callback]) => {
        allCallbacks[key] = [...(allCallbacks[key] ?? []), callback];
        if (key === 'syncBetslip') betslipContext.syncModulesBetslipData();
      });
    };
  };

  componentDidMount() {
    const { betslipContext } = this.props;
    const { addOrRemoveFromBetslip } = betslipContext;

    window.bettingActions = {
      ...(window.bettingActions || {}),
      addOrRemoveFromBetslip,
    };
  }

  private handleBetslipToggle = (): void => {
    const { betslipContext } = this.props;
    const { open: betslipOpen, toggleBetslip } = betslipContext;
    const event = !betslipOpen ? TRACKING_EVENTS.BET_SLIP_OPEN : TRACKING_EVENTS.BET_SLIP_CLOSE;

    trackingActions.eventTracking({
      event,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[event],
      eventLabel: '',
    });

    toggleBetslip();
  };

  private renderEmptyBetslip = (): React.JSX.Element => {
    const { betslipContext, labelActions, repub } = this.props;
    const { open: betslipOpen } = betslipContext;
    const isGZRepub = repub === REPUBS.GZ.name;

    return (
      <div className={[styles.emptyBetslip, betslipOpen ? styles.open : ''].join(' ')}>
        {!isGZRepub && (
          <div className={styles.iconWrapper}>
            <EmptyBetslip />
          </div>
        )}

        <Title className={styles.title} tag={Header.SPAN} title={labelActions.get('NOTHING_TO_SEE_HERE')} />

        <p className={styles.info}>{labelActions.get('BETSLIP_IS_EMPTY')}</p>
      </div>
    );
  };

  private renderBetslipContent = (): React.JSX.Element => {
    const { betslipContext, labelActions } = this.props;
    const { count, loading, open: betslipOpen, selectedBookie } = betslipContext;

    if (loading) {
      return (
        <div className={[styles.emptyBetslip, betslipOpen ? styles.open : ''].join(' ')} role="status">
          <div className={styles.loadingIconWrapper}>
            <LoadingBetslip />
          </div>

          <Title className={styles.title} tag={Header.SPAN} title={labelActions.get('ALMOST_THERE')} />

          <p className={styles.info}>{labelActions.get('LOADING_BETS')}</p>
        </div>
      );
    }

    if (count >= 1 && selectedBookie) {
      return <MultiBetslip />;
    }

    return this.renderEmptyBetslip();
  };

  private renderTitle = () => {
    const { betslipContext, labelActions } = this.props;
    const { count } = betslipContext;

    switch (true) {
      case count === 1:
        return labelActions.get('BETSLIP_SINGLE');
      case count > 1:
        return labelActions.get('BETSLIP_MULTI');
      default:
        return labelActions.get('BETSLIP');
    }
  };

  render(): React.JSX.Element {
    const { betslipContext, className } = this.props;
    const { count, loading, open: betslipOpen } = betslipContext;

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureException(error);
          return null;
        }}
      >
        <div
          aria-busy={loading}
          aria-hidden={!betslipOpen}
          aria-label="Betslip"
          className={[styles.betslipContainer, betslipOpen ? styles.open : '', className || ''].join(' ')}
          data-betslip-count={count}
          data-betslip-open={betslipOpen ? 'true' : 'false'}
          data-testid="betslip"
          inert={!betslipOpen}
          role="region"
        >
          <div className={styles.header}>
            <div className={styles.content}>
              <span
                aria-label={`${count} ${count === 1 ? 'bet' : 'bets'} selected`}
                aria-live="polite"
                className={styles.counter}
                role="status"
              >
                {count}
              </span>
              <span className={styles.title}>{this.renderTitle()}</span>
            </div>
            <span
              aria-label="Close betslip"
              className={styles.closeIcon}
              data-testid="close-betslip"
              onClick={this.handleBetslipToggle}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  this.handleBetslipToggle();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Icon height={32} icon="Close" width={32} />
            </span>
          </div>
          {this.renderBetslipContent()}
          <ButtonSection />
          <ClickoutErrorModal />
        </div>
      </ErrorBoundary>
    );
  }
}

export function withContext<T extends WithContext = WithContext>(Component: React.ComponentType<T>) {
  const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
    const betslipContext = useBetslipContext();
    const labelActions = useLabelActions();

    return (
      <Component
        {...(props as T)}
        betslipContext={betslipContext}
        labelActions={labelActions}
        repub={betslipContext.repub}
      />
    );
  };

  const displayName = Component.displayName || Component.name || 'Betslip';
  WrappedComponent.displayName = `withContext(${displayName})`;

  return WrappedComponent;
}

const BetslipWithContextAndStoreSlices = withContext(Betslip);

export default BetslipWithContextAndStoreSlices;
